import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import AnchoredPopover from "./AnchoredPopover";
import { formatTimeAmPm, minutesToTimeInput, timeInputToMinutes } from "./data";

/**
 * Time field for the daily schedule — a drop-in for `<input type="time">`
 * (same "HH:MM" value contract) that doesn't hand the picker to the
 * browser. Chrome's native popover is a blue system control that ignores
 * the app's type and colour; this one is ours:
 *
 *   • the field is a plain text input showing "07:00 am"; typing is
 *     accepted and parsed leniently on blur / Enter — "7", "7:30", "7.30pm",
 *     "19:30", "0730", "7 pm" all work; anything else reverts;
 *   • ↑ / ↓ nudge the time by 5 minutes;
 *   • the clock button (or a click in the field) opens a popover with three
 *     scroll columns — hour · minute · am/pm — the current value on a
 *     purple tile, "Now" and "Done" underneath.
 *
 * The popover is an AnchoredPopover (portalled, kept on screen, flips
 * above the field when there's no room below) — the entry modal scrolls
 * and would clip anything positioned inside it. Closes on outside click,
 * Escape, Enter or Done.
 */

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS = ["am", "pm"];
const POP_W = 264;
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

// Lenient text → minutes-past-midnight; null when it isn't a time.
export const parseTimeText = (raw) => {
  const s = (raw || "").toLowerCase().replace(/[\s.]/g, "");
  if (!s) return null;
  const m = s.match(/^(\d{1,2})(?::?(\d{2}))?(am|pm|a|p)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const period = m[3] ? m[3][0] : null;
  if (min > 59) return null;
  if (period) {
    if (h < 1 || h > 12) return null;
    if (h === 12) h = 0;
    if (period === "p") h += 12;
  } else if (h > 23) {
    return null;
  }
  return h * 60 + min;
};

const split = (mins) => {
  const h = Math.floor(mins / 60);
  return { h12: h % 12 === 0 ? 12 : h % 12, minute: mins % 60, period: h < 12 ? "am" : "pm" };
};
const join = ({ h12, minute, period }) => ((h12 % 12) + (period === "pm" ? 12 : 0)) * 60 + minute;

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 8V4.2M8 8l2.6 2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const Column = ({ label, items, selected, render, onPick }) => {
  const ref = useRef(null);
  // Bring the selected tile into the middle of the column when it changes.
  useLayoutEffect(() => {
    const col = ref.current;
    if (!col) return;
    const el = col.querySelector(".is-selected");
    if (!el) return;
    col.scrollTop = el.offsetTop - col.clientHeight / 2 + el.clientHeight / 2;
  }, [selected]);
  return (
    <div className="dstp-col">
      <span className="dstp-col-label">{label}</span>
      <ul className="dstp-list" role="listbox" aria-label={label} ref={ref}>
        {items.map((it) => (
          <li key={it}>
            <button
              type="button"
              className={`dstp-opt${it === selected ? " is-selected" : ""}`}
              role="option"
              aria-selected={it === selected}
              onClick={() => onPick(it)}
            >
              {render(it)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
Column.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  selected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  render: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

const TimePicker = ({ id, value, onChange, disabled, inputClassName, placeholder }) => {
  const minutes = timeInputToMinutes(value);
  const display = minutes == null ? "" : formatTimeAmPm(minutes);

  const [text, setText] = useState(display);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  // `editing` is true only while the user has TYPED something not yet
  // committed — focus alone doesn't count. Outside that window the text
  // mirrors `value`, so a pick in the popover (which updates the parent)
  // shows up in the field even though the field still has focus.
  useEffect(() => {
    if (!editing) setText(display);
  }, [display, editing]);

  // Every way of setting the time lands here. It also ends any typing
  // session and paints the new value straight away, so a later blur has
  // nothing stale to re-parse and write back over the pick.
  const emit = useCallback(
    (mins) => {
      setEditing(false);
      setText(mins == null ? "" : formatTimeAmPm(mins));
      onChange(mins == null ? "" : minutesToTimeInput(mins));
    },
    [onChange]
  );

  // Commit what was typed: a parseable time is emitted, anything else
  // snaps the text back to the last good value. Nothing typed → nothing
  // to do (the text already mirrors the value).
  const commitText = () => {
    if (!editing) return;
    const parsed = parseTimeText(text);
    if (text.trim() === "") emit(null);
    else if (parsed != null) emit(parsed);
    else {
      setText(display);
      setEditing(false);
    }
  };

  // Column picks build on the current value; an empty field starts from a
  // sensible morning default so the first pick reads as a full time.
  const base = minutes == null ? split(8 * 60) : split(minutes);
  const pick = (patch) => emit(join({ ...base, ...patch }));

  const nudge = (delta) => {
    const from = minutes == null ? 8 * 60 : minutes;
    emit((from + delta + 1440) % 1440);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitText();
      setOpen(false);
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      nudge(e.key === "ArrowUp" ? 5 : -5);
    }
  };

  return (
    <div className={`dstp${disabled ? " is-disabled" : ""}`} ref={wrapRef}>
      <input
        id={id}
        ref={inputRef}
        className={`${inputClassName} dstp-input`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          setEditing(true);
          setText(e.target.value);
        }}
        onBlur={commitText}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="dstp-toggle"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label={open ? "Close time picker" : "Open time picker"}
        aria-haspopup="dialog"
        aria-expanded={open}
        tabIndex={-1}
      >
        <ClockIcon />
      </button>

      <AnchoredPopover
        open={open}
        anchorRef={wrapRef}
        onClose={close}
        align="left"
        className="dstp-pop"
        style={{ width: POP_W }}
        role="dialog"
        aria-label="Pick a time"
        onMouseDown={(e) => e.preventDefault()} /* keep focus in the field */
      >
            <div className="dstp-cols">
              <Column
                label="Hour"
                items={HOURS}
                selected={minutes == null ? null : base.h12}
                render={pad2}
                onPick={(h12) => pick({ h12 })}
              />
              <Column
                label="Minute"
                items={MINUTES}
                selected={minutes == null ? null : base.minute}
                render={pad2}
                onPick={(minute) => pick({ minute })}
              />
              <Column
                label="am / pm"
                items={PERIODS}
                selected={minutes == null ? null : base.period}
                render={(p) => p}
                onPick={(period) => pick({ period })}
              />
            </div>
            <div className="dstp-foot">
              <button
                type="button"
                className="dstp-now"
                onClick={() => {
                  const d = new Date();
                  emit(d.getHours() * 60 + d.getMinutes());
                }}
              >
                Now
              </button>
              <button type="button" className="dstp-done" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
      </AnchoredPopover>
    </div>
  );
};

TimePicker.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string.isRequired, // "HH:MM" or ""
  onChange: PropTypes.func.isRequired, // ("HH:MM" | "") => void
  disabled: PropTypes.bool,
  // Chrome of the host form's inputs, so the field matches its neighbours.
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
};

TimePicker.defaultProps = {
  id: undefined,
  disabled: false,
  inputClassName: "ds4-input",
  placeholder: "Select time",
};

export default TimePicker;
